import { useSizing } from 'hooks/useSizing';
import { handleMoveShouldSetResponderCapture } from 'lib/handleMoveShouldSetResponderCapture';
import { theme } from 'lib/theme';
import React from 'react';
import { Insets, StyleProp, View, ViewStyle } from 'react-native';
import {
  KeyboardAvoidingView,
  KeyboardAwareScrollView,
  KeyboardAwareScrollViewProps,
} from 'react-native-keyboard-controller';
import type { KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';

type Props = {
  scrollView?: boolean;
  scrollIndicatorInsets?: Insets;
  scrollEnabled?: boolean;
  children: React.ReactNode;
  keyboardAvoiding?: boolean;
  scrollViewContentContainerStyle?: StyleProp<ViewStyle>;
  contentPaddingTop?: number;
  contentPaddingBottom?: number;
  backgroundColor?: string;
  onLayout?: KeyboardAwareScrollViewProps['onLayout'];
  onScroll?: KeyboardAwareScrollViewProps['onScroll'];
  scrollViewRef?: React.RefObject<KeyboardAwareScrollViewRef | null>;
  withSafeAreaTop?: boolean;
  withSafeAreaBottom?: boolean;
  withSafeAreaLeft?: boolean;
  withSafeAreaRight?: boolean;
  gap?: number;
};

export const KeyboardAware = ({
  scrollView = false,

  scrollIndicatorInsets,
  scrollEnabled,
  children,
  keyboardAvoiding,
  scrollViewContentContainerStyle,

  contentPaddingTop = theme.paddingPageVertical,
  contentPaddingBottom = theme.paddingPageVertical,
  gap = theme.paddingPageVertical,

  backgroundColor,
  onLayout,
  onScroll,
  scrollViewRef,
  withSafeAreaTop,
  withSafeAreaBottom,
  withSafeAreaLeft = true,
  withSafeAreaRight = true,
}: Props): React.ReactElement => {
  const { headerHeight, safeAreaBottom, safeAreaLeft, safeAreaRight } =
    useSizing();
  const paddingTop =
    (withSafeAreaTop ? headerHeight || 0 : 0) + contentPaddingTop;
  const paddingBottom =
    (withSafeAreaBottom ? safeAreaBottom : 0) + contentPaddingBottom;

  if (scrollView) {
    return (
      <KeyboardAwareScrollView
        ref={scrollViewRef}
        enabled={keyboardAvoiding}
        onScroll={onScroll}
        scrollEnabled={scrollEnabled}
        scrollIndicatorInsets={scrollIndicatorInsets}
        onMoveShouldSetResponderCapture={handleMoveShouldSetResponderCapture}
        onLayout={onLayout}
        contentContainerStyle={[
          {
            paddingTop,
            paddingBottom,
            paddingLeft: withSafeAreaLeft ? safeAreaLeft : 0,
            paddingRight: withSafeAreaRight ? safeAreaRight : 0,
          },
          scrollViewContentContainerStyle,
        ]}
        style={{
          flex: 1,
          backgroundColor: backgroundColor || theme.colors.background,
        }}
      >
        {/**
         * bug: Need to add gap to a new view. KeyboardAwareScrollView adds
         * a component after children causing an additional gap
         * */}
        <View style={{ gap }}>{children}</View>
      </KeyboardAwareScrollView>
    );
  }

  return (
    <>
      <KeyboardAvoidingView
        enabled={keyboardAvoiding}
        style={{
          flex: 1,
          paddingTop,
          paddingLeft: safeAreaLeft,
          paddingRight: safeAreaRight,
          gap,
        }}
        behavior="padding"
      >
        {children}
      </KeyboardAvoidingView>
      {(withSafeAreaBottom || !!contentPaddingBottom) && (
        <View
          style={{
            height: paddingBottom,
          }}
        ></View>
      )}
    </>
  );
};
